/**
 * openLibraryService — the only module in the app that knows Open Library's
 * URLs and response shapes. Everything above this layer (hooks, components)
 * only ever sees the normalized Book model from lib/bookModel.js, so
 * swapping this file for a ScribbleReads backend later requires no UI
 * changes — see useBookSearch.js and components/books/*.
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || process.env.VITE_API_URL;
const WORKS_BASE = 'https://openlibrary.org/works';
const SUBJECTS_BASE = 'https://openlibrary.org/subjects';
const COVERS_BASE = 'https://covers.openlibrary.org/b/id';

/**
 * @param {number} coverId
 * @param {'S'|'M'|'L'} [size]
 */
export function buildCoverUrl(coverId, size = 'M') {
  if (!coverId) return null;
  return `${COVERS_BASE}/${coverId}-${size}.jpg`;
}

/**
 * Open Library search doc -> normalized Book (see lib/bookModel.js).
 */
export function normalizeBook(doc) {
  const isbns = doc.isbn ?? doc.isbns ?? [];
  const workId = (doc.key ?? doc.workId ?? doc.openLibraryWorkId ?? '').replace('/works/', '') || null;
  const authors = doc.author_name ?? doc.authors ?? (doc.author ? [doc.author] : []);
  const subjects = doc.subject ?? doc.subjects ?? [];

  const toDisplayString = (value) => {
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    return value?.name ?? value?.title ?? value?.label ?? '';
  };

  return {
    id: doc.id ?? doc.key ?? `book_${doc.title}`,
    title: doc.title ?? 'Untitled',
    authors: (Array.isArray(authors) ? authors : [authors]).map(toDisplayString).filter(Boolean),
    coverId: doc.cover_i ?? doc.coverId ?? null,
    coverUrl: doc.coverUrl ?? doc.cover_url ?? buildCoverUrl(doc.cover_i ?? doc.coverId, 'M'),
    isbn10: isbns.filter((i) => i.length === 10),
    isbn13: isbns.filter((i) => i.length === 13),
    publishYear: doc.first_publish_year ?? doc.publishYear ?? doc.publishedYear ?? null,
    firstPublishYear: doc.first_publish_year ?? doc.publishYear ?? doc.publishedYear ?? null,
    subjects: (Array.isArray(subjects) ? subjects : [subjects]).map(toDisplayString).filter(Boolean).slice(0, 8),
    publisher: Array.isArray(doc.publisher) ? toDisplayString(doc.publisher[0]) : toDisplayString(doc.publisher) || null,
    description: typeof doc.description === 'string' ? doc.description : null,
    tone: doc.tone ?? doc.avatarTone ?? null,
    totalPages: doc.totalPages ?? doc.pageCount ?? null,
    rating: doc.rating ?? doc.averageRating ?? null,
    openLibraryWorkId: workId,
    openLibraryEditionId: doc.edition_key?.[0] ?? null,
    ebookAccess: doc.ebook_access ?? doc.ebookAccess ?? 'no_ebook',
    internetArchiveId: doc.ia?.[0] ?? doc.internetArchiveId ?? null,
    source: doc.source ?? 'scribblereads',
  };
}

/**
 * The real, legitimate URL to read/borrow a book, or null when Open Library
 * doesn't indicate any reading source — callers must never render a "Read
 * Now" action when this returns null.
 *
 * @param {import('../lib/bookModel').Book} book
 */
export function getReadableUrl(book) {
  if (!book?.internetArchiveId) return null;
  if (book.ebookAccess === 'public') return `https://archive.org/details/${book.internetArchiveId}`;
  if (book.ebookAccess === 'borrowable') return `https://openlibrary.org/borrow/ia/${book.internetArchiveId}`;
  return null;
}

function normalizeDescription(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && typeof raw.value === 'string') return raw.value;
  return null;
}

/**
 * Search Open Library. Throws on network/HTTP failure so react-query can
 * surface an error state; callers pass an AbortSignal for stale-request
 * cancellation.
 *
 * @param {string} query
 * @param {{ page?: number, limit?: number, signal?: AbortSignal }} [options]
 * @returns {Promise<{ books: import('../lib/bookModel').Book[], numFound: number, page: number, hasMore: boolean }>}
 */
export async function searchBooks(query, { page = 1, limit = 20, signal, accessToken } = {}) {
  const trimmed = query.trim();
  if (!trimmed) return { books: [], numFound: 0, page, hasMore: false };

  if (!API_BASE_URL) throw new Error('The API URL is not configured.');
  const url = new URL(`${API_BASE_URL}/books/search`);
  url.searchParams.set('q', trimmed);
  url.searchParams.set('page', String(page));
  url.searchParams.set('limit', String(limit));

  const res = await fetch(url.toString(), {
    signal,
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new Error(`Book search failed (${res.status})`);
  const data = await res.json();
  console.log(`[Books API] GET /books/search?q=${trimmed}&page=${page}&limit=${limit} response:`, data);

  const result = data?.data ?? data;
  const rawBooks = result?.books ?? result?.items ?? result?.results ?? result?.docs ?? [];
  const books = rawBooks.map(normalizeBook);
  const pagination = data?.meta?.pagination ?? result?.pagination ?? {};
  const responsePage = pagination.page ?? page;
  const responseLimit = pagination.limit ?? limit;
  const numFound = result?.total ?? result?.totalCount ?? result?.numFound ?? pagination.total ?? books.length;
  const hasMore = pagination.hasMore ?? (responsePage * responseLimit < numFound);
  return { books, numFound, page: responsePage, hasMore };
}

export async function getTrendingBooks({ limit = 20, signal, accessToken } = {}) {
  if (!API_BASE_URL) throw new Error('The API URL is not configured.');

  const url = new URL(`${API_BASE_URL}/books/trending`);
  url.searchParams.set('limit', String(limit));

  const res = await fetch(url.toString(), {
    signal,
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new Error(`Trending books failed (${res.status})`);
  const payload = await res.json();
  console.log(`[Books API] GET /books/trending?limit=${limit} response:`, payload);

  const result = payload?.data ?? payload;
  const rawBooks = result?.books ?? result?.items ?? result?.results ?? [];
  return rawBooks.map((book) => ({
    ...normalizeBook(book),
    rating: book.rating ?? book.averageRating ?? null,
    stat: book.stat ?? book.trendingStat ?? null,
  }));
}

export async function getBookDetails(bookId, { signal, accessToken } = {}) {
  if (!API_BASE_URL) throw new Error('The API URL is not configured.');
  if (!bookId) throw new Error('A book ID is required.');

  const response = await fetch(`${API_BASE_URL}/books/${encodeURIComponent(bookId)}`, {
    signal,
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!response.ok) throw new Error(`Book details failed (${response.status})`);

  const payload = await response.json();
  console.log(`[Books API] GET /books/${bookId} response:`, payload);
  const rawBook = payload?.data?.book || payload?.data || payload?.book || payload;
  return normalizeBook(rawBook);
}

/**
 * Fetch work-level details (description, subjects) not present in search
 * results, and merge them into an already-known Book.
 *
 * @param {string} workId - e.g. "OL45804W" (no "/works/" prefix)
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function getWorkDetails(workId, { signal } = {}) {
  const res = await fetch(`${WORKS_BASE}/${workId}.json`, { signal });
  if (!res.ok) throw new Error(`Open Library work lookup failed (${res.status})`);
  const data = await res.json();

  return {
    title: data.title ?? null,
    description: normalizeDescription(data.description),
    subjects: data.subjects?.slice(0, 8) ?? null,
    coverId: data.covers?.find((c) => c > 0) ?? null,
    coverUrl: buildCoverUrl(data.covers?.find((c) => c > 0), 'L'),
  };
}

/**
 * Cold-load ebook availability for a work reached directly by URL (no
 * search-doc hint carrying ebook_access/ia already). Checks a handful of
 * editions and returns the first one with a real Internet Archive id.
 *
 * @param {string} workId
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ebookAccess: string, internetArchiveId: string|null }>}
 */
export async function getEbookAvailability(workId, { signal } = {}) {
  const res = await fetch(`${WORKS_BASE}/${workId}/editions.json?limit=5`, { signal });
  if (!res.ok) return { ebookAccess: 'no_ebook', internetArchiveId: null };
  const data = await res.json();
  const readable = (data.entries ?? []).find((e) => e.ocaid);
  if (!readable) return { ebookAccess: 'no_ebook', internetArchiveId: null };
  return {
    ebookAccess: readable.ebook_access ?? 'borrowable',
    internetArchiveId: readable.ocaid,
  };
}

/**
 * Subject-based fallback for "similar books" when the local catalog doesn't
 * have enough overlap matches. Normalizes Open Library's subjects/{slug}.json
 * `works[]` into the same Book shape as search results.
 *
 * @param {string} subject
 * @param {{ limit?: number, signal?: AbortSignal }} [options]
 */
export async function searchBySubject(subject, { limit = 8, signal } = {}) {
  const slug = subject.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (!slug) return [];

  const url = new URL(`${SUBJECTS_BASE}/${slug}.json`);
  url.searchParams.set('limit', String(limit));

  const res = await fetch(url.toString(), { signal });
  if (!res.ok) return [];
  const data = await res.json();

  return (data.works ?? []).map((w) => ({
    id: w.key,
    title: w.title ?? 'Untitled',
    authors: (w.authors ?? []).map((a) => a.name).filter(Boolean),
    coverId: w.cover_id ?? null,
    coverUrl: buildCoverUrl(w.cover_id, 'M'),
    subjects: [subject],
    openLibraryWorkId: (w.key ?? '').replace('/works/', '') || null,
    source: 'openlibrary',
  }));
}
